import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/TextField';
import { authClient } from './authClient';
import { AuthLayout, FormError } from './AuthLayout';
import { COPY, describeAuthError, describeCallbackError, retryAfterSeconds } from './authErrors';
import { GoogleButton } from './GoogleButton';
import { useSession } from './session';

export function SignInScreen() {
  const session = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(() =>
    describeCallbackError(params.get('error'), 'sign-in'),
  );
  const [busy, setBusy] = useState(false);

  if (session.status === 'signed_in') return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!navigator.onLine) {
      setError(COPY.offline);
      return;
    }
    setBusy(true);
    try {
      let retryAfter: number | undefined;
      const { error: err } = await authClient.signIn.email(
        { email: email.trim(), password },
        {
          onError: (ctx) => {
            retryAfter = retryAfterSeconds(ctx.response);
          },
        },
      );
      if (err) {
        setError(describeAuthError({ ...err, retryAfter }, 'sign-in'));
        return;
      }
      await session.refresh();
      navigate('/', { replace: true });
    } catch {
      setError(COPY.offline);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Sign in">
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          maxLength={254}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          maxLength={128}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <FormError message={error} />
        <Button type="submit" variant="primary" block disabled={busy}>
          Sign in
        </Button>
      </form>
      <GoogleButton errorPath="/sign-in" />
      <p className="text-sm text-muted">{COPY.inviteOnly}</p>
    </AuthLayout>
  );
}
